/*
 * -----------------------------------------------------------------------
 *  <copyright file="custom-report-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { GridColumnDateFilter } from '@components/core/grid-filters';
import { ExtraMenuItem } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useGridCells, useTranslation } from 'hooks';
import { ReportDefinitionFields } from 'queries/generated/fragments.generated';

interface CustomReportGridProps {
    reports: ReportDefinitionFields[];
    actions: any;
    title?: string;
    extraMenuItems?: ExtraMenuItem[];
    className?: string;
}

interface CustomReportGridItem extends ReportDefinitionFields {
    fullName: string;
    _createdByString?: string;
    _updatedByString?: string;
}

const CustomReportGrid = ({ reports, actions, title, extraMenuItems, className }: CustomReportGridProps) => {
    const { t } = useTranslation();
    const { MaxLengthCell, DateCell } = useGridCells();
    const [gridItems, setGridItems] = useState([] as CustomReportGridItem[]);

    const getGridItems = (): CustomReportGridItem[] => {
        const items = reports.map((report: ReportDefinitionFields) => {
            const fullName = report.includeInternalRisks ? `INTERNAL ${report.name}` : report.name;

            return {
                ...report,
                fullName,
            } as CustomReportGridItem;
        });

        return restructuredUpdatedCreatedBy(items);
    };

    useEffect(() => {
        if (reports) setGridItems(getGridItems());
    }, [reports]);

    return (
        <ExpandableGridV2
            title={title}
            className={className}
            customKey="custom-report-grid"
            id="customReportGrid"
            key="customReportGrid"
            items={gridItems}
            rowActions={actions}
            hideCheckbox
            hideExpand
            groupable={false}
            scrollable="scrollable"
            reorderable
            pageable={false}
            resizable
            initialSort={[{ field: 'fullName', dir: 'asc' }]}
            detail={null}
            columns={[
                {
                    field: 'fullName',
                    label: t('admin:customReportName'),
                    id: 'custom-report-name',
                    minWidth: 180,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    field: 'description',
                    label: t('admin:customReportDescription'),
                    id: 'custom-report-description',
                    minWidth: 180,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    label: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'custom-report-createdby',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    field: 'created',
                    id: 'custom-report-created',
                    filter: 'date',
                    filterUi: GridColumnDateFilter,
                    minWidth: 180,
                    cells: { data: DateCell },
                },
                {
                    label: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'custom-report-updatedby',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'custom-report-updated',
                    filter: 'date',
                    filterUi: GridColumnDateFilter,
                    minWidth: 190,
                    cells: { data: DateCell },
                },
                {
                    field: 'recordId',
                    label: 'ID',
                    minWidth: 120,
                    id: 'custom-report-id',
                    filter: 'numeric',
                    showByDefault: false,
                },
            ]}
            extraMenuItems={extraMenuItems}
        />
    );
};

export default CustomReportGrid;
